import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, ListGroup, Row, Spinner, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import { TemplateComposicao } from '../../model/composicao/TemplateComposicao';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import { TemplateComposicaoViewModel } from '../../viewmodel/composicao/TemplateComposicaoViewModel';

const DetalhesComposicao: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [template, setTemplate] = useState<TemplateComposicao | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) {
            carregarTemplate();
        }
    }, [id]);

    useEffect(() => {
        if (template) {
            setBreadcrumbs(createBreadcrumbs.fromRoot([
                { path: '/composicoes', breadcrumb: 'Composições' },
                { path: `/composicoes/${id}`, breadcrumb: template.nome }
            ]));
        }
    }, [template, id, setBreadcrumbs]);

    const carregarTemplate = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await TemplateComposicaoViewModel.getById(id);
            setTemplate(data);
        } catch (error) {
            toast.error(BackendErrorMessage(error));
            navigate('/composicoes');
        } finally {
            setLoading(false);
        }
    };

    if (loading || !template) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-1">{template.nome}</h2>
                            <p className="text-muted">{template.descricao || 'Sem descrição'}</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/composicoes')}>
                            <i className="bi bi-arrow-left me-2"></i>
                            Voltar
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row className="g-4">
                <Col md={4}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0">Informações Gerais</h6>
                        </Card.Header>
                        <ListGroup variant="flush">
                            <ListGroup.Item>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted">Status:</span>
                                    <Badge bg={template.ativo ? 'success' : 'secondary'}>
                                        {template.ativo ? 'Ativo' : 'Inativo'}
                                    </Badge>
                                </div>
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted">Tipo de Banca:</span>
                                    <strong>{template.tipoBanca.nome}</strong>
                                </div>
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted">Categoria:</span>
                                    <Badge bg="secondary">{template.categoria}</Badge>
                                </div>
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted">Versão:</span>
                                    <strong>v{template.versao}</strong>
                                </div>
                            </ListGroup.Item>
                            {template.totalTitularesMinimo && (
                                <ListGroup.Item>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Total Titulares:</span>
                                        <strong>
                                            {template.totalTitularesMinimo === template.totalTitularesMaximo
                                                ? template.totalTitularesMinimo
                                                : `${template.totalTitularesMinimo} - ${template.totalTitularesMaximo}`}
                                        </strong>
                                    </div>
                                </ListGroup.Item>
                            )}
                            {template.criadoPor && (
                                <ListGroup.Item>
                                    <div className="small">
                                        <div className="text-muted">Criado por:</div>
                                        <div>{template.criadoPor}</div>
                                        <div className="text-muted">{new Date(template.criadoEm).toLocaleString()}</div>
                                    </div>
                                </ListGroup.Item>
                            )}
                        </ListGroup>
                    </Card>
                </Col>

                <Col md={8}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0">Regras de Vínculos Titulares</h6>
                        </Card.Header>
                        <Card.Body>
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>Vínculo</th>
                                        <th>Mínimo</th>
                                        <th>Máximo</th>
                                        <th>Obrigatório</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {template.regrasVinculos.map(regra => (
                                        <tr key={regra.id}>
                                            <td>{regra.vinculo.nome}</td>
                                            <td>{regra.minimo}</td>
                                            <td>{regra.maximo}</td>
                                            <td>
                                                {regra.obrigatorio ? (
                                                    <Badge bg="success">Sim</Badge>
                                                ) : (
                                                    <Badge bg="secondary">Não</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0">Regras de Papéis</h6>
                        </Card.Header>
                        <Card.Body>
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>Papel</th>
                                        <th>Mínimo</th>
                                        <th>Máximo</th>
                                        <th>Vínculos Permitidos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {template.regrasPapeis.map(regra => (
                                        <tr key={regra.id}>
                                            <td>{regra.papel.nome}</td>
                                            <td>{regra.minimo}</td>
                                            <td>{regra.maximo}</td>
                                            <td>
                                                {regra.vinculosPermitidos.length > 0 ? (
                                                    regra.vinculosPermitidos.map(v => v.nome).join(', ')
                                                ) : (
                                                    <span className="text-muted">Todos</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default DetalhesComposicao;
